import type { ReactNode } from "react";
import { Table } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { cn } from "@/lib/utils";
import {
  listingStatusMeta,
  STATUS_DOT_CLASS,
  STATUS_TONE_CLASS,
} from "@/lib/listing-status";

/**
 * The panel's design primitives.
 *
 * Everything a page is built from lives here, and nothing else. Before this
 * file existed, four screens answered "what is a card?" four different ways —
 * a flat `Panel` with no radius, an antd `Card` with 14px corners and a shadow,
 * a shadcn `Card` off a 4px token, and a bare `div` with a border — so the app
 * read as three products stitched together even though each screen was fine on
 * its own.
 *
 * The rules:
 *
 *   1. **One surface.** `.surface` (white, hairline, 8px) is the only card. A
 *      panel's header is a ruled band, not a floating title, so sections read as
 *      sections.
 *   2. **One rhythm.** `PageShell` owns the outer padding and max width;
 *      `PageHeader` owns the title block; `.surface-body` owns inner padding.
 *      A page never re-invents any of those.
 *   3. **One control height.** `.field` and the antd theme both resolve to 38px,
 *      so a hand-rolled `<input>` and an antd `<Input>` sit level.
 *   4. **Status is a pill with a dot, never colour alone** (WCAG 1.4.1).
 */

/* ── Layout ───────────────────────────────────────────────────────────────── */

/** The content column: capped width, consistent padding, nothing else. */
export function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("page-shell", className)}>{children}</div>;
}

/**
 * The title block at the top of every authenticated page.
 *
 * One shape for all of them: optional eyebrow, one `h1`, one sentence of
 * orientation, actions on the right. The dashboard and Team page used to each
 * hand-roll this and the vehicles pages had no header at all, which is why
 * moving between them felt like changing applications.
 */
export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
}: {
  title: string;
  description?: ReactNode;
  eyebrow?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-primary">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="text-2xl font-semibold leading-tight tracking-tight text-ink sm:text-[28px]">{title}</h1>
        {description ? (
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-ink-muted">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/* ── Surfaces ─────────────────────────────────────────────────────────────── */

/** The only card in the system. */
export function Panel({
  title,
  description,
  action,
  children,
  className,
  bodyClassName,
  flush,
}: {
  title?: string;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Drop the inner padding — for a table or a list that draws its own rules. */
  flush?: boolean;
}) {
  return (
    <section className={cn("surface", className)}>
      {title || action ? (
      <div className="surface-head">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight text-ink">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">{description}</p>
          ) : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </div>
      ) : null}
      <div className={cn(flush ? "" : "surface-body", bodyClassName)}>{children}</div>
    </section>
  );
}

export function Empty({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="py-10 text-center">
      {title ? <p className="text-sm font-medium text-ink">{title}</p> : null}
      <p className="mx-auto mt-1 max-w-sm text-sm leading-relaxed text-ink-muted">{children}</p>
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : "Something went wrong";
  return (
    <div role="alert" className="rounded-r border-l-[3px] border-danger bg-danger-wash px-4 py-3">
      <p className="text-sm font-medium text-danger">{message}</p>
      <p className="mt-0.5 text-sm text-ink-muted">Reload the page to try again.</p>
    </div>
  );
}

/* ── Controls ─────────────────────────────────────────────────────────────── */

/**
 * A button. `quiet` is the bordered secondary, `danger` the destructive one.
 *
 * `size="sm"` exists because the table row actions were being sized with a
 * `className` override that fought the base `min-h-11` — which is why a 44px
 * touch target was showing 28px text.
 */
export function Button({
  variant = "default",
  size = "md",
  icon,
  loading,
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "quiet" | "danger" | "ghost";
  size?: "sm" | "md";
  icon?: ReactNode;
  loading?: boolean;
}) {
  const base =
    "inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-45";
  const sizing =
    size === "sm" ? "min-h-8 px-2.5 py-1 text-xs" : "min-h-10 px-4 py-2 text-sm";
  const tone = {
    default: "bg-primary text-white hover:bg-primary-deep",
    quiet: "border border-line-strong bg-raised text-ink hover:bg-sunken",
    danger: "border border-danger-line bg-raised text-danger hover:bg-danger-wash",
    ghost: "text-ink-muted hover:bg-sunken hover:text-ink",
  }[variant];

  return (
    <button {...props} className={cn(base, sizing, tone, className)} disabled={loading || props.disabled}>
      {loading ? <LoadingOutlined spin /> : icon}
      {children}
    </button>
  );
}

export function Field({
  id,
  label,
  hint,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { id: string; label: string; hint?: string }) {
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input id={id} {...props} className="field" />
      {hint ? <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/* ── Status ───────────────────────────────────────────────────────────────── */

/**
 * A pill with a dot, so state is never carried by hue alone (WCAG 1.4.1).
 *
 * The label/tone table lives in `lib/listing-status.ts` — this file must only
 * export components for Fast Refresh to keep working.
 */
export function StatusTag({ status }: { status: string }) {
  const meta = listingStatusMeta(status);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11.5px] font-medium",
        STATUS_TONE_CLASS[meta.tone],
      )}
    >
      <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT_CLASS[meta.tone])} />
      {meta.label}
    </span>
  );
}

/* ── Tables ───────────────────────────────────────────────────────────────── */

/**
 * antd's Table with the panel's frame around it.
 *
 * The wrapper carries `.table-shell`, which is where the header band, row
 * height, hover tint and last-row rule live. Without it every table looked like
 * an unstyled antd demo even when the rest of the page did not.
 */
export function DataTable<T extends object>({
  columns,
  rows,
  loading,
  total,
  page = 1,
  pageSize = 20,
  onPageChange,
  rowKey = "id",
  emptyText = "Nothing here yet",
}: {
  columns: ColumnsType<T>;
  rows: T[];
  loading?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  rowKey?: string;
  emptyText?: string;
}) {
  const paginated = total !== undefined && total > pageSize;

  return (
    <div className="table-shell">
      <Table<T>
        rowKey={rowKey}
        columns={columns}
        dataSource={rows}
        loading={loading}
        locale={{ emptyText }}
        scroll={{ x: 860 }}
        pagination={
          paginated
            ? {
                current: page,
                pageSize,
                total,
                showSizeChanger: false,
                onChange: onPageChange,
                showTotal: (count) => `${count} bikes`,
              }
            : false
        }
      />
    </div>
  );
}

/* ── Verification ─────────────────────────────────────────────────────────── */

/** Named, never badged — §2/§8: verification is never purchasable and never
 *  implied where it did not happen. `NONE` is spelled out. */
export function VerificationLabel({
  level,
  verifiedAt,
  className,
}: {
  level: "NONE" | "PHONE_CONFIRMED" | "IDENTITY_CHECKED" | "BUSINESS_CHECKED";
  verifiedAt?: string | null;
  className?: string;
}) {
  const COPY: Record<typeof level, string> = {
    NONE: "Not verified",
    PHONE_CONFIRMED: "Phone confirmed",
    IDENTITY_CHECKED: "ID checked",
    BUSINESS_CHECKED: "Business checked",
  };
  const checked = level !== "NONE";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs font-medium",
        checked ? "text-primary" : "text-ink-muted",
        className,
      )}
      title={
        verifiedAt
          ? `Checked ${new Date(verifiedAt).toLocaleDateString()}`
          : "No check has been carried out"
      }
    >
      <span aria-hidden className={checked ? "text-primary" : "text-ink-faint"}>
        {checked ? "✓" : "○"}
      </span>
      {COPY[level]}
    </span>
  );
}
